import type {
  Building,
  BuildingId,
  CitizenId,
  ConstructionStage,
  GridCoord,
  ZoneType
} from '../../types/core';
import { buildingId } from '../../utils/id';
import { pickDefinitionForZone } from '../../world/buildingDefinitions';
import { buildingName } from '../../world/names';

const stageThresholds: readonly [ConstructionStage, number][] = [
  ['cleared', 0],
  ['foundations', 0.14],
  ['framework', 0.34],
  ['scaffolding', 0.56],
  ['roofing', 0.78],
  ['completed', 1]
];

export function constructionStage(progress: number): ConstructionStage {
  const match = [...stageThresholds].reverse().find(([, threshold]) => progress >= threshold);
  return match?.[0] ?? 'cleared';
}

export function createBuilding(
  zone: ZoneType,
  cells: readonly GridCoord[],
  day: number,
  rng: () => number,
  ordinal: number
): Building {
  const definition = pickDefinitionForZone(zone, rng, cells.length);
  const id = buildingId(`${zone}-${day}-${ordinal}-${cells.map((cell) => `${cell.x}-${cell.z}`).join('-')}`);

  return {
    id,
    name: buildingName(zone, rng),
    zone,
    kind: definition.kind,
    cells,
    createdAtDay: day,
    construction: zone === 'park' ? 0.75 : 0,
    stage: zone === 'park' ? 'roofing' : 'cleared',
    level: 1,
    residents: [],
    workers: [],
    visitors: [],
    popularity: zone === 'park' ? 45 : 28 + Math.round(rng() * 25),
    production: zone === 'workspace' ? 10 : 0,
    mood: 'Calm',
    operatingStatus: 'Building'
  };
}

export function updateConstruction(buildings: readonly Building[], deltaMinutes: number): Building[] {
  return buildings.map((building) => {
    if (building.stage === 'completed') {
      return building;
    }

    const speed = building.zone === 'park' ? 0.08 : 0.036;
    const construction = Math.min(1, building.construction + speed * (deltaMinutes / 60));
    const stage = constructionStage(construction);

    return {
      ...building,
      construction,
      stage,
      operatingStatus: stage === 'completed' ? 'Open' : 'Building',
      mood: stage === 'completed' ? 'Happy' : 'Busy'
    };
  });
}

export function attachResidents(
  buildings: readonly Building[],
  buildingIdToResidents: ReadonlyMap<BuildingId, readonly CitizenId[]>
): Building[] {
  return buildings.map((building) => ({
    ...building,
    residents: buildingIdToResidents.get(building.id) ?? building.residents
  }));
}

export function attachWorkers(
  buildings: readonly Building[],
  buildingIdToWorkers: ReadonlyMap<BuildingId, readonly CitizenId[]>
): Building[] {
  return buildings.map((building) => ({
    ...building,
    workers: buildingIdToWorkers.get(building.id) ?? building.workers
  }));
}
